import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Menu, X, LogOut, LayoutDashboard, Phone } from "lucide-react";
import { Button } from "./ui/button";
import BrandMark from "./BrandMark";

const navLinks = [
  { href: "/#about",        label: "Meet the Expert" },
  { href: "/#how-it-works", label: "The Process" },
  { href: "/#who-we-help",  label: "Who We Help" },
  { href: "/#get-started",  label: "Ready When You Are" },
  { href: "/#stories",      label: "Client Stories" },
  { href: "/#tech",         label: "The Technology" },
  { href: "/#referrals",    label: "Referral Network" },
];

export default function Navbar() {
  // The navbar shows the logo + Get Started CTA + hamburger at every screen
  // size. All section links live inside the menu panel that opens when the
  // hamburger is clicked.
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const isHome = location.pathname === "/";

  // When the page is the homepage we use plain hash anchors so the browser
  // does a same-page scroll. On other pages we send the user back to /#...
  const hrefFor = (l) => (isHome ? l.href.replace("/", "") : l.href);

  return (
    <nav className="fixed top-0 w-full z-50 glass-nav bg-background/80 border-b border-border" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <BrandMark className="h-8" />
        </Link>

        <div className="flex items-center gap-2 md:gap-4">
          {/* Contact Otto — stacked label + number on md+, icon only on mobile */}
          <a
            href="tel:+19193331277"
            className="hidden md:inline-flex items-center gap-2.5 group"
            data-testid="nav-contact-phone"
          >
            <Phone className="w-5 h-5 text-[#007AFF] shrink-0" strokeWidth={2} />
            <span className="flex flex-col leading-tight">
              <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-muted-foreground group-hover:text-foreground transition-colors">Contact Otto Now</span>
              <span className="text-sm font-bold text-[#007AFF] group-hover:text-[#3395FF] transition-colors">(919) 333-1277</span>
            </span>
          </a>
          <a
            href="tel:+19193331277"
            className="md:hidden inline-flex items-center justify-center w-9 h-9 text-[#007AFF] hover:bg-secondary transition-colors rounded-none"
            aria-label="Call Otto at (919) 333-1277"
            data-testid="nav-contact-phone-mobile"
          >
            <Phone className="w-4 h-4" strokeWidth={2} />
          </a>

          <Link to="/book">
            <Button className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none px-5 py-2 text-sm font-bold tracking-wide" data-testid="nav-book-cta">
              Get Started
            </Button>
          </Link>
          <button
            onClick={() => setOpen(!open)}
            className="p-2 hover:bg-secondary transition-colors"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="nav-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background px-6 py-4 space-y-1" data-testid="nav-menu-panel">
          {navLinks.map((l) => (
            <a
              key={l.label}
              href={hrefFor(l)}
              onClick={() => setOpen(false)}
              className="block py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`nav-menu-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
          {user && (
            <div className="pt-3 border-t border-border space-y-2 mt-2">
              <Link to={user.role === "admin" ? "/admin" : "/client"} onClick={() => setOpen(false)}>
                <Button variant="ghost" size="sm" className="w-full justify-start gap-2" data-testid="nav-menu-dashboard">
                  <LayoutDashboard className="w-4 h-4" /> {user.role === "admin" ? "Admin" : "Dashboard"}
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { logout(); setOpen(false); }}
                className="w-full justify-start gap-2"
                data-testid="nav-menu-logout"
              >
                <LogOut className="w-4 h-4" /> Log out
              </Button>
            </div>
          )}
        </div>
      )}
    </nav>
  );
}
